import { DATE_FORMATS } from '@web-platform/shared-universal-constants/src/dateFormats';
import {
  formatDateNonlocalised,
  parseDateTime,
  DATE_TYPE,
  getDateType as getDateTypeForClient,
  isPastDay,
  isPastMonth,
} from '@web-platform/shared-universal-utils-frontend-localisation';
import { isBefore } from 'date-fns/isBefore';

import type { DateString } from '@web-platform/shared-universal-types/src/dateString';
import type { Maybe } from '@web-platform/shared-universal-types-utils/src/utils';

const getDateType = (date: Maybe<DateString>) =>
  date === 'anytime' ? DATE_TYPE.ANYTIME : getDateTypeForClient(date);

type TripInfo = {
  tripType: 'one-way' | 'return';
  outboundDate: DateString;
  inboundDate?: DateString;
  isAnytime?: boolean;
};

// Convert to standard specific date or downgrade to anytime (be the same with logic in server side). Otherwise return the input data without any changes
export const specificDateHandler = ({ inboundDate, outboundDate, tripType }: TripInfo) => {
  const result: {
    tripType: 'return' | 'one-way';
    outboundDate: DateString;
    inboundDate?: DateString | null;
  } = {
    tripType,
    inboundDate,
    outboundDate,
  };

  const dateType = getDateType(result.outboundDate);
  if (dateType !== DATE_TYPE.SPECIFIC_DATE) {
    return {
      ...result,
    };
  }

  // Step 1. Downgrade to one-way trip if inboundDate is not a specific date or is a past date or is before outboundDate. Then tripType and inboundDate must be correct.
  const dateTypeOfInbound = getDateType(result.inboundDate);
  if (
    dateTypeOfInbound !== DATE_TYPE.SPECIFIC_DATE ||
    !result.inboundDate ||
    isPastDay(result.inboundDate) ||
    isBefore(parseDateTime(result.inboundDate), parseDateTime(result.outboundDate))
  ) {
    result.tripType = 'one-way';
    result.inboundDate = null;
  }

  // Step 2. Downgrade to anytime if outboundDate is a past date. Then outboundDate must be correct
  if (isPastDay(result.outboundDate)) {
    result.outboundDate = 'anytime';
    if (result.tripType === 'return') {
      result.inboundDate = 'anytime';
    }
  }

  return {
    ...result,
  };
};

// Convert to standard month date if the outboundDate is month. Otherwise return the input data without any changes
export const monthHandler = ({ inboundDate, outboundDate, tripType }: TripInfo) => {
  const dateType = getDateType(outboundDate);
  if (dateType !== DATE_TYPE.MONTH) {
    return {
      tripType,
      inboundDate,
      outboundDate,
    };
  }

  const dateTypeOfInbound = getDateType(inboundDate);
  const thisMonth = formatDateNonlocalised(new Date(), DATE_FORMATS.YEAR_MONTH_FULL_DASHED);
  const dateCollection: {
    outboundDate: DateString;
    inboundDate: DateString;
  } = {
    inboundDate: inboundDate || outboundDate,
    outboundDate,
  };

  //  === Below is special logic of one-way  ===
  if (tripType !== 'return') {
    if (isPastMonth(dateCollection.outboundDate)) {
      dateCollection.outboundDate = thisMonth;
    }
    return {
      tripType,
      outboundDate: dateCollection.outboundDate,
      inboundDate: null,
    };
  }

  //  === Below is special logic of return  ===
  // Step 1. Convert inboundDate to outboundDate if inboundDate is not a month
  if (dateTypeOfInbound !== DATE_TYPE.MONTH) {
    dateCollection.inboundDate = outboundDate;
  }
  // Step 2. Convert all past month to current month
  if (isPastMonth(dateCollection.outboundDate)) {
    dateCollection.outboundDate = thisMonth;
  }
  if (isPastMonth(dateCollection.inboundDate)) {
    dateCollection.inboundDate = thisMonth;
  }
  // Step 3. Convert outboundDate to inboundDate if inboundDate is before outboundDate
  if (
    isBefore(parseDateTime(dateCollection.inboundDate), parseDateTime(dateCollection.outboundDate))
  ) {
    dateCollection.outboundDate = dateCollection.inboundDate;
  }
  return {
    tripType,
    ...dateCollection,
  };
};

// Convert to standard anytime date if the outboundDate is not specific or month. Otherwise return the input data without any changes
export const anytimeHandler = ({ inboundDate, outboundDate, tripType }: TripInfo) => {
  const dateType = getDateType(outboundDate);

  if (dateType !== DATE_TYPE.SPECIFIC_DATE && dateType !== DATE_TYPE.MONTH) {
    return {
      tripType,
      outboundDate: '',
      inboundDate: tripType === 'return' ? '' : null,
      isAnytime: outboundDate !== undefined, // Unselected date, outboundDate is not issued so it is undefined
    };
  }

  return {
    tripType,
    outboundDate,
    inboundDate,
  };
};

const compose =
  (...fns: Function[]) =>
  (args: any) =>
    fns.reduce((acc, fn) => fn(acc), args);

export const handler = ({ inboundDate, outboundDate, tripType }: TripInfo) => {
  // Only return or one-way trip will be handled
  if (tripType !== 'one-way' && tripType !== 'return')
    return { tripType, outboundDate, inboundDate };

  // Handle tripInfo one by one in the order of specificDateHandler，monthHandler and anytimeHandler
  return compose(
    specificDateHandler,
    monthHandler,
    anytimeHandler,
  )({ tripType, inboundDate, outboundDate });
};
